/**
 * Login Page — Google/Apple via Clerk + email/password
 */

import { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useSignIn } from '@clerk/clerk-react';
import coreskanLogo from '../assets/coreskan-logo.png';

// ─── Animated Topographic Background ────────────────────────────────────────

function TopoBackground() {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    let animationId: number;
    let time = 0;

    const resize = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
    };
    resize();
    window.addEventListener('resize', resize);

    const draw = () => {
      const w = canvas.width;
      const h = canvas.height;
      time += 0.003;

      ctx.fillStyle = '#0a0a0a';
      ctx.fillRect(0, 0, w, h);

      // Draw topographic contour lines
      const lineCount = 30;
      for (let i = 0; i < lineCount; i++) {
        const baseY = (h / lineCount) * i;
        const alpha = 0.04 + Math.sin(i * 0.3 + time * 0.5) * 0.02;
        ctx.strokeStyle = `rgba(79, 124, 255, ${alpha})`;
        ctx.lineWidth = 0.8;
        ctx.beginPath();

        for (let x = 0; x <= w; x += 3) {
          const noise1 = Math.sin(x * 0.003 + i * 0.5 + time) * 30;
          const noise2 = Math.sin(x * 0.007 - i * 0.3 + time * 1.3) * 15;
          const noise3 = Math.cos(x * 0.002 + i * 0.8 - time * 0.7) * 20;
          const y = baseY + noise1 + noise2 + noise3;

          if (x === 0) ctx.moveTo(x, y);
          else ctx.lineTo(x, y);
        }
        ctx.stroke();
      }

      // Draw subtle grid dots
      const dotSpacing = 60;
      for (let x = dotSpacing; x < w; x += dotSpacing) {
        for (let y = dotSpacing; y < h; y += dotSpacing) {
          const drift = Math.sin(x * 0.01 + y * 0.01 + time * 0.8) * 3;
          const alpha = 0.03 + Math.sin(x * 0.005 + y * 0.005 + time) * 0.015;
          ctx.fillStyle = `rgba(79, 124, 255, ${alpha})`;
          ctx.beginPath();
          ctx.arc(x + drift, y + drift, 1, 0, Math.PI * 2);
          ctx.fill();
        }
      }

      animationId = requestAnimationFrame(draw);
    };

    draw();

    return () => {
      cancelAnimationFrame(animationId);
      window.removeEventListener('resize', resize);
    };
  }, []);

  return (
    <canvas
      ref={canvasRef}
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 0,
        pointerEvents: 'none',
      }}
    />
  );
}

const clerkEnabled = !!import.meta.env.VITE_CLERK_PUBLISHABLE_KEY;

function OAuthButtons() {
  const { signIn, isLoaded } = useSignIn();
  const [busy, setBusy] = useState(false);
  const [oauthError, setOauthError] = useState('');

  const handleOAuth = async (strategy: 'oauth_google' | 'oauth_apple') => {
    if (!isLoaded || !signIn || busy) return;
    setBusy(true);
    setOauthError('');
    try {
      await signIn.authenticateWithRedirect({
        strategy,
        redirectUrl: '/sso-callback',
        redirectUrlComplete: '/',
      });
    } catch (err: any) {
      console.error('OAuth error:', err);
      if (err?.status === 429) {
        setOauthError('Too many attempts — please wait a few minutes and try again.');
      } else {
        setOauthError('Sign-in failed. Please try again.');
      }
      setBusy(false);
    }
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      {oauthError && <div style={styles.error}>{oauthError}</div>}
      <button
        onClick={() => handleOAuth('oauth_google')}
        disabled={!isLoaded || busy}
        style={{ ...styles.oauthBtn, opacity: busy ? 0.6 : 1 }}
      >
        <svg width="18" height="18" viewBox="0 0 48 48" style={{ marginRight: 10, flexShrink: 0 }}>
          <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/>
          <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/>
          <path fill="#FBBC05" d="M10.53 28.59a14.5 14.5 0 0 1 0-9.18l-7.98-6.19a24.01 24.01 0 0 0 0 21.56l7.98-6.19z"/>
          <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/>
        </svg>
        {busy ? 'Redirecting...' : 'Continue with Google'}
      </button>
      <button
        onClick={() => handleOAuth('oauth_apple')}
        disabled={!isLoaded || busy}
        style={{ ...styles.oauthBtn, opacity: busy ? 0.6 : 1 }}
      >
        <svg width="18" height="18" viewBox="0 0 24 24" fill="#000" style={{ marginRight: 10, flexShrink: 0 }}>
          <path d="M17.05 20.28c-.98.95-2.05.88-3.08.4-1.09-.5-2.08-.48-3.24 0-1.44.62-2.2.44-3.06-.4C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z"/>
        </svg>
        {busy ? 'Redirecting...' : 'Continue with Apple'}
      </button>
    </div>
  );
}

export function LoginPage() {
  const { login, isAuthenticated, isAdmin, isLoading: authLoading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [flash, setFlash] = useState('');
  const [loading, setLoading] = useState(false);

  // Show flash message from signup redirect
  useEffect(() => {
    const state = location.state as { flash?: string } | null;
    if (state?.flash) {
      setFlash(state.flash);
      // Clear the state so it doesn't persist on refresh
      window.history.replaceState({}, document.title);
    }
  }, [location.state]);

  useEffect(() => {
    if (isAuthenticated) {
      navigate(isAdmin ? '/admin' : '/portal', { replace: true });
    }
  }, [isAuthenticated, isAdmin, navigate]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setFlash('');
    setLoading(true);
    try {
      await login(email, password);
    } catch (err: any) {
      const msg = err.message || 'Login failed';
      if (msg.includes('pending approval')) {
        navigate('/pending');
        return;
      }
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  // Don't flash the login form while auth is still resolving
  if (authLoading || isAuthenticated) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '100vh', background: '#0a0a0a' }}>
        <TopoBackground />
      </div>
    );
  }

  return (
    <div style={styles.container}>
      <TopoBackground />
      <div style={styles.card}>
        <div style={styles.logo}>
          <img src={coreskanLogo} alt="CoreSkan" style={{ height: 40, marginBottom: 8 }} />
        </div>

        {clerkEnabled && (
          <>
            <OAuthButtons />
            <div style={styles.divider}>
              <div style={styles.dividerLine} />
              <span style={styles.dividerText}>or</span>
              <div style={styles.dividerLine} />
            </div>
          </>
        )}

        <form onSubmit={handleSubmit} style={styles.form}>
          {flash && <div style={styles.flash}>{flash}</div>}
          {error && <div style={styles.error}>{error}</div>}

          <div style={styles.field}>
            <label style={styles.label}>Email</label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              style={styles.input}
              placeholder="admin@coreskan.com"
              required
            />
          </div>

          <div style={styles.field}>
            <label style={styles.label}>Password</label>
            <div style={styles.passwordWrapper}>
              <input
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                style={{ ...styles.input, width: '100%', paddingRight: 44 }}
                placeholder="Enter your password"
                required
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                style={styles.showPasswordBtn}
                tabIndex={-1}
              >
                {showPassword ? (
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#64748b" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94"/>
                    <path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19"/>
                    <line x1="1" y1="1" x2="23" y2="23"/>
                  </svg>
                ) : (
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#64748b" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
                    <circle cx="12" cy="12" r="3"/>
                  </svg>
                )}
              </button>
            </div>
          </div>

          <button type="submit" style={styles.button} disabled={loading}>
            {loading ? 'Signing in...' : 'Sign In'}
          </button>
        </form>

        <div style={styles.footer}>
          Don't have an account?{' '}
          <Link to="/signup" style={styles.link}>Sign up</Link>
        </div>
      </div>
    </div>
  );
}

const styles: Record<string, React.CSSProperties> = {
  container: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: '100vh',
    background: '#0a0a0a',
    position: 'relative' as const,
  },
  card: {
    background: 'rgba(26, 26, 26, 0.85)',
    backdropFilter: 'blur(16px)',
    borderRadius: 12,
    padding: 'clamp(20px, 5vw, 40px) clamp(16px, 4vw, 36px)',
    width: 420,
    maxWidth: '90vw',
    boxShadow: '0 25px 50px rgba(0, 0, 0, 0.5)',
    border: '1px solid rgba(51, 51, 51, 0.6)',
    position: 'relative' as const,
    zIndex: 1,
  },
  logo: {
    textAlign: 'center' as const,
    marginBottom: 32,
  },
  title: {
    color: '#f1f5f9',
    fontSize: 28,
    fontWeight: 600,
    margin: 0,
    letterSpacing: '0.08em',
    textTransform: 'lowercase' as const,
  },
  oauthBtn: {
    width: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: '#fff',
    color: '#1f2937',
    border: 'none',
    borderRadius: 8,
    padding: '12px 0',
    fontSize: 15,
    fontWeight: 600,
    cursor: 'pointer',
    minHeight: 44,
  },
  divider: {
    display: 'flex',
    alignItems: 'center',
    margin: '20px 0',
    gap: 12,
  },
  dividerLine: {
    flex: 1,
    height: 1,
    background: '#333333',
  },
  dividerText: {
    color: '#475569',
    fontSize: 12,
  },
  form: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: 20,
  },
  field: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: 6,
  },
  label: {
    color: '#94a3b8',
    fontSize: 13,
    fontWeight: 500,
  },
  input: {
    background: '#0d0d0d',
    border: '1px solid #333333',
    borderRadius: 8,
    padding: '10px 14px',
    color: '#f1f5f9',
    fontSize: 16,
    outline: 'none',
    boxSizing: 'border-box' as const,
    width: '100%',
    minHeight: 44,
  },
  passwordWrapper: {
    position: 'relative' as const,
  },
  showPasswordBtn: {
    position: 'absolute' as const,
    right: 8,
    top: '50%',
    transform: 'translateY(-50%)',
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    padding: 4,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    minWidth: 44,
    minHeight: 44,
  },
  button: {
    background: '#4F7CFF',
    color: '#fff',
    border: 'none',
    borderRadius: 8,
    padding: '12px 0',
    fontSize: 15,
    fontWeight: 600,
    cursor: 'pointer',
    marginTop: 4,
    minHeight: 44,
  },
  error: {
    background: '#7f1d1d',
    color: '#fca5a5',
    padding: '10px 14px',
    borderRadius: 8,
    fontSize: 13,
  },
  flash: {
    background: '#052e16',
    color: '#86efac',
    padding: '10px 14px',
    borderRadius: 8,
    fontSize: 13,
  },
  footer: {
    textAlign: 'center' as const,
    marginTop: 24,
    color: '#64748b',
    fontSize: 13,
  },
  link: {
    color: '#4F7CFF',
    textDecoration: 'none',
  },
};
